import { useEffect, useState } from "react";
import { Share2, Copy, X, Check } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { enableGardenShare, disableGardenShare, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ShareGardenDialog({ open, onOpenChange, garden, onChange }) {
    const [token, setToken] = useState(garden?.share_token || null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setToken(garden?.share_token || null);
    }, [garden?.share_token, garden?.id]);

    const url = token ? `${window.location.origin}/share/${token}` : "";

    const enable = async () => {
        if (!garden?.id) return;
        setBusy(true);
        try {
            const data = await enableGardenShare(garden.id);
            setToken(data.share_token);
            onChange?.({ share_token: data.share_token });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        if (!garden?.id) return;
        setBusy(true);
        try {
            await disableGardenShare(garden.id);
            setToken(null);
            onChange?.({ share_token: null });
            toast.success("Share link disabled");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Link copied");
        } catch {
            toast.error("Could not copy — copy manually");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="share-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-[var(--vm-accent)]" /> Share garden
                    </DialogTitle>
                    <DialogDescription>
                        Anyone with the link can view your garden, beds, structures and crops — but cannot edit.
                    </DialogDescription>
                </DialogHeader>

                {!token ? (
                    <div className="text-sm text-[var(--vm-text-muted)] py-2">
                        Sharing is currently <strong className="text-[var(--vm-text)]">off</strong>.
                        Generate a private link to share a read-only view.
                    </div>
                ) : (
                    <div className="space-y-3 py-2">
                        <div className="flex gap-2">
                            <Input
                                value={url} readOnly
                                data-testid="share-link-input"
                                className="bg-[var(--vm-bg)] border-[var(--vm-border)] font-mono text-xs"
                                onFocus={(e) => e.target.select()}
                            />
                            <Button
                                type="button" onClick={copy}
                                data-testid="share-copy-button"
                                variant="outline"
                                className="rounded-md border-[var(--vm-border)]"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                        </div>
                        <p className="text-xs text-[var(--vm-text-muted)]">
                            Anyone with this link can view the garden. Disable below to revoke access.
                        </p>
                        <div className="pt-2 border-t border-[var(--vm-border)]">
                            <div className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-2">
                                Harvest Party share card
                            </div>
                            <a
                                href={`${window.location.origin}/harvest/${token}`}
                                target="_blank" rel="noreferrer"
                                data-testid="share-harvest-card-link"
                                className="inline-flex items-center gap-2 text-sm text-[var(--vm-accent)] hover:underline"
                            >
                                Open Harvest Card →
                            </a>
                            <p className="text-xs text-[var(--vm-text-muted)] mt-1">
                                A social-ready PNG of your season's harvest — download or embed anywhere.
                            </p>
                        </div>
                    </div>
                )}

                <DialogFooter className="flex gap-2 sm:justify-between">
                    {token ? (
                        <Button
                            type="button" variant="ghost" disabled={busy}
                            onClick={disable}
                            data-testid="share-disable-button"
                            className="text-[var(--vm-secondary)]"
                        >
                            <X className="w-4 h-4 mr-2" /> Disable link
                        </Button>
                    ) : (
                        <Button
                            type="button" disabled={busy}
                            onClick={enable}
                            data-testid="share-enable-button"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                        >
                            <Share2 className="w-4 h-4 mr-2" /> Generate share link
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
